import React, { useContext } from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames/bind';
import { Placeholder, Rating } from '@thd-olt-component-react/core-ui';
import {
  arrayOf,
  extend,
  number,
  params,
  shape,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { StorePodContext } from '../../StorePod.context';
import { RatingDetailed } from './RatingDetailed/RatingDetailed';
import styles from './store-pod-rating.module.scss';

const cx = classNames.bind(styles);

export const StorePodRating = ({ className, disableTooltip, onClick }) => {
  const { loading, store } = useContext(StorePodContext);
  const { channel } = useContext(ExperienceContext);

  const { storeId } = store || {};
  const options = {
    variables: {
      storeId
    },
    skip: !storeId
  };

  const { data, error } = useDataModel('storeReviews', options);

  const {
    AverageOverallRating: averageOverallRating,
    NotRecommendedCount: notRecommendedCount,
    RatingDistribution: ratingDistribution,
    RecommendedCount: recommendedCount,
    TotalReviewCount: totalReviewCount
  } = data?.storeReviews?.Includes?.Products?.store?.FilteredReviewStatistics || {};

  const isLoading = loading || !totalReviewCount || !averageOverallRating;

  if (isLoading) {
    return (
      <div className={cx(className, 'store-pod-rating')} data-testid="store-pod-rating-loader">
        <Placeholder className={cx('store-pod-rating__placeholder')} height="15px" />
      </div>
    );
  }

  const HoverContent = React.forwardRef(function Content(props, ref) {
    return (
      <RatingDetailed
        averageOverallRating={averageOverallRating}
        notRecommendedCount={notRecommendedCount}
        ratingDistribution={ratingDistribution}
        recommendedCount={recommendedCount}
        totalReviewCount={totalReviewCount}
      />
    );
  });

  return (
    <button onClick={onClick} type="submit">
      <Tooltip
        channel={channel}
        className={cx('store-pod-rating__tooltip')}
        content={<HoverContent />}
        disableTooltip={disableTooltip}
        interactive
        placement="bottom-start"
      >
        <div className={cx(className, 'store-pod-rating')} data-testid="store-pod-rating">
          <Rating className={cx('store-pod-rating__rating')} value={Number(averageOverallRating)} />
          <span className={cx('store-pod-rating__count')}>({totalReviewCount})</span>
        </div>
      </Tooltip>
    </button>
  );
};

StorePodRating.propTypes = {
  className: string,
  disableTooltip: bool,
  onClick: func
};

StorePodRating.defaultProps = {
  className: null,
  disableTooltip: false,
  onClick: () => {}
};

StorePodRating.displayName = 'StorePodRating';

StorePodRating.dataModel = extend({
  storeReviews: params({
    storeId: stringType().isRequired()
  }).shape({
    Includes: shape({
      Products: shape({
        store: shape({
          FilteredReviewStatistics: shape({
            AverageOverallRating: stringType(),
            NotRecommendedCount: number(),
            RatingDistribution: arrayOf(shape({
              RatingValue: number(),
              Count: number(),
            })),
            RecommendedCount: number(),
            TotalReviewCount: number()
          }),
        })
      })
    })
  })
});
